import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail, User, Sparkles, CheckCircle2, Loader2, X,
  TrendingUp, Briefcase, GraduationCap, Phone, MessageCircle,
  DollarSign, Send, ShieldCheck, ArrowRight,
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { CountrySelect } from '../common/CountrySelect';
import { PhoneInput } from '../common/PhoneInput';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_SRC = `${API}/api/media/varotrade_official_emblem.png`;

const EXPERIENCE_OPTIONS = [
  { id: 'beginner',     ar: 'مبتدئ',    en: 'Beginner',     icon: GraduationCap, hint_ar: 'جديد على عالم التداول',          hint_en: 'New to trading' },
  { id: 'intermediate', ar: 'متوسط',    en: 'Intermediate', icon: TrendingUp,    hint_ar: 'لديّ خبرة لبضعة أشهر/سنوات',     hint_en: 'Some experience' },
  { id: 'professional', ar: 'محترف',   en: 'Professional', icon: Briefcase,     hint_ar: 'متداول محترف بدوام كامل',         hint_en: 'Full-time trader' },
];

const INVESTMENT_RANGES = [
  { id: '100-500',  ar: '$100 – $500',     en: '$100 – $500' },
  { id: '500-1k',   ar: '$500 – $1,000',   en: '$500 – $1,000' },
  { id: '1k-5k',    ar: '$1,000 – $5,000', en: '$1,000 – $5,000' },
  { id: '5k-25k',   ar: '$5,000 – $25,000', en: '$5,000 – $25,000' },
  { id: '25k+',     ar: 'أكثر من $25,000', en: '$25,000+' },
];

const CONTACT_OPTIONS = [
  { id: 'whatsapp', ar: 'واتساب',          en: 'WhatsApp',  icon: MessageCircle },
  { id: 'phone',    ar: 'مكالمة هاتفية',   en: 'Phone Call', icon: Phone },
  { id: 'email',    ar: 'البريد الإلكتروني', en: 'Email',     icon: Mail },
];

/* ──────────────────────────────────────────────────────────────────────
 * Reusable bits — defined OUTSIDE the modal so they remount-stable.
 * ────────────────────────────────────────────────────────────────────── */
const FieldLabel = ({ children, required }) => (
  <label className="block text-[12.5px] font-bold text-ink-700 mb-1.5 tracking-tight">
    {children}{required && <span className="text-flame-600 ms-1">*</span>}
  </label>
);

const ChoiceCard = ({ active, onClick, icon: Icon, title, hint, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`w-full text-start p-3.5 rounded-2xl border-2 transition-all flex items-start gap-3 group ${
      active
        ? 'border-flame-500 bg-flame-50/70 shadow-[0_4px_18px_rgba(216,83,8,0.14)]'
        : 'border-ink-100 bg-white hover:border-flame-200 hover:bg-flame-50/30'
    }`}
  >
    <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 transition-colors ${
      active ? 'bg-flame-500 text-white' : 'bg-ink-50 text-ink-700 group-hover:bg-flame-100 group-hover:text-flame-600'
    }`}>
      <Icon className="w-5 h-5" strokeWidth={1.8} />
    </div>
    <div className="flex-1 min-w-0">
      <div className={`text-[14px] font-extrabold leading-tight ${active ? 'text-flame-700' : 'text-ink-900'}`}>{title}</div>
      {hint && <div className="text-[11.5px] text-ink-500 mt-0.5 leading-snug">{hint}</div>}
    </div>
    {active && (
      <CheckCircle2 className="w-5 h-5 text-flame-500 shrink-0" strokeWidth={2.4} />
    )}
  </button>
);

const PillChoice = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`px-4 py-2.5 rounded-full text-[13px] font-bold border-2 transition-all whitespace-nowrap ${
      active
        ? 'bg-ink-900 text-white border-ink-900'
        : 'bg-white text-ink-700 border-ink-100 hover:border-ink-300'
    }`}
  >
    {children}
  </button>
);

/* ──────────────────────────────────────────────────────────────────────
 * The modal itself.
 * ────────────────────────────────────────────────────────────────────── */
export const LeadCaptureModal = ({ open, onClose, initialEmail = '' }) => {
  const { isRTL } = useLanguage();
  const t = (ar, en) => (isRTL ? ar : en);

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [done, setDone] = useState(false);

  const [form, setForm] = useState({
    full_name: '',
    email: initialEmail || '',
    phone: '',
    country: 'SA',
    experience: '',
    other_broker: null,           // true | false | null
    other_broker_name: '',
    investment_range: '',
    preferred_contact: '',
    notes: '',
  });

  // Reset state when re-opened (sync initialEmail from hero into form)
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setStep(1);
      setDone(false);
      setErrorMsg('');
      setForm((f) => ({ ...f, email: initialEmail || f.email || '' }));
    }
    prevOpenRef.current = open;
  }, [open, initialEmail]);

  // Body scroll lock — uses the global mechanism (role=dialog) — also escape to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // ─── Validators ──────────────────────────────────────────────────────
  const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
  const step1Valid = form.full_name.trim().length >= 2 && isEmail(form.email) && form.phone.replace(/\D/g, '').length >= 6;
  const step2Valid = !!form.experience && form.other_broker !== null && !!form.investment_range;
  const step3Valid = !!form.preferred_contact;

  const goNext = () => {
    setErrorMsg('');
    if (step === 1 && !step1Valid) {
      setErrorMsg(t('يرجى تعبئة الاسم والبريد الإلكتروني ورقم الهاتف.',
                    'Please fill name, email and phone.')); return;
    }
    if (step === 2 && !step2Valid) {
      setErrorMsg(t('يرجى اختيار مستوى الخبرة والمبلغ والإجابة عن سؤال الوسيط.',
                    'Choose experience, investment & answer the broker question.')); return;
    }
    setStep(step + 1);
  };

  const submit = async () => {
    if (!step3Valid) {
      setErrorMsg(t('يرجى اختيار طريقة التواصل المُفضّلة.', 'Pick a preferred contact method.'));
      return;
    }
    setSubmitting(true); setErrorMsg('');
    try {
      const res = await fetch(`${API}/api/leads/inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Server error');
      }
      setDone(true);
    } catch (e) {
      setErrorMsg(t('تعذر إرسال البيانات. حاول مجدداً.', 'Could not submit. Try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        role="dialog"
        aria-modal="true"
        data-modal="open"
        data-testid="lead-modal"
        dir={isRTL ? 'rtl' : 'ltr'}
        className="fixed inset-0 z-[100] flex items-stretch md:items-center justify-center bg-black/55 backdrop-blur-sm overflow-y-auto"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.96 }}
          transition={{ type: 'spring', stiffness: 280, damping: 26 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full md:max-w-[680px] md:my-8 bg-white md:rounded-3xl shadow-[0_24px_80px_rgba(20,15,10,0.35)] overflow-hidden flex flex-col"
        >
          {/* ── Decorative top band ─────────────────────────────────── */}
          <div className="relative h-32 md:h-36 bg-gradient-to-br from-flame-500 via-flame-600 to-orange-700 overflow-hidden shrink-0">
            <div className="absolute inset-0 opacity-25" style={{
              backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(255,255,255,0.4) 0, transparent 40%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.3) 0, transparent 40%)',
            }} />
            <motion.div
              initial={{ y: -10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 }}
              className="absolute inset-0 flex items-center justify-center"
            >
              <img src={LOGO_SRC} alt="VAROTRADES" className="h-9 md:h-10 w-auto drop-shadow-[0_4px_12px_rgba(0,0,0,0.25)]" />
            </motion.div>

            <button
              type="button"
              onClick={onClose}
              data-testid="lead-modal-close"
              className={`absolute top-3 ${isRTL ? 'left-3' : 'right-3'} w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-md grid place-items-center text-white transition`}
              aria-label="Close"
            >
              <X className="w-4.5 h-4.5" />
            </button>

            {/* Progress indicator */}
            {!done && (
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2">
                {[1,2,3].map((s) => (
                  <div key={s} className={`h-1.5 rounded-full transition-all ${
                    s === step ? 'w-8 bg-white' : s < step ? 'w-5 bg-white/85' : 'w-5 bg-white/35'
                  }`} />
                ))}
              </div>
            )}
          </div>

          {/* ── Body ─────────────────────────────────────────────────── */}
          <div className="px-5 md:px-8 py-6 md:py-7 overflow-y-auto overscroll-contain" data-lenis-prevent data-lenis-prevent-wheel data-lenis-prevent-touch style={{ maxHeight: 'calc(100vh - 160px)', WebkitOverflowScrolling: 'touch' }}>
            {done ? (
              <motion.div
                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                className="text-center py-6"
                data-testid="lead-modal-success"
              >
                <motion.div
                  initial={{ scale: 0 }} animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 220, damping: 14, delay: 0.05 }}
                  className="w-20 h-20 mx-auto rounded-full bg-green-50 grid place-items-center mb-4 ring-8 ring-green-50/40"
                >
                  <CheckCircle2 className="w-10 h-10 text-green-600" strokeWidth={2.2} />
                </motion.div>
                <h3 className="text-2xl md:text-3xl font-black text-ink-900 mb-2">
                  {t('شكراً لك!', 'Thank you!')}
                </h3>
                <p className="text-ink-700 text-[14.5px] leading-relaxed max-w-md mx-auto">
                  {t(
                    'تم استلام بياناتك. سيتواصل معك أحد خبراء VAROTRADES خلال 24 ساعة عبر القناة التي اخترتها.',
                    'We received your details. A VAROTRADES expert will reach out within 24 hours on your preferred channel.'
                  )}
                </p>
                <div className="mt-6 flex items-center justify-center gap-2 text-[11.5px] text-ink-500">
                  <ShieldCheck className="w-3.5 h-3.5 text-flame-500" />
                  {t('بياناتك مشفّرة ومحمية وفق معايير FSC Mauritius.',
                     'Your data is encrypted and protected per FSC Mauritius standards.')}
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  data-testid="lead-modal-done"
                  className="mt-7 inline-flex items-center gap-2 h-12 px-7 rounded-full bg-ink-900 text-white text-[14px] font-bold hover:bg-flame-600 transition"
                >
                  {t('رائع، أغلق', 'Great, close')}
                  <ArrowRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
                </button>
              </motion.div>
            ) : (
              <>
                <AnimatePresence mode="wait">
                  {step === 1 && (
                    <motion.div
                      key="s1"
                      initial={{ opacity: 0, x: isRTL ? -24 : 24 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: isRTL ? 24 : -24 }}
                      transition={{ duration: 0.28 }}
                    >
                      <div className="mb-5">
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-flame-50 text-flame-700 text-[10.5px] font-bold uppercase tracking-[0.15em] mb-2">
                          <Sparkles className="w-3 h-3" /> {t('الخطوة 1 من 3', 'Step 1 of 3')}
                        </div>
                        <h2 className="text-[22px] md:text-[26px] font-black text-ink-900 leading-tight">
                          {t('لنتعرّف عليك أكثر', "Let's get acquainted")}
                        </h2>
                        <p className="text-ink-500 text-[13px] mt-1">
                          {t('بضع معلومات أساسية لنبدأ.', 'Just a few basics to get started.')}
                        </p>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <FieldLabel required>{t('الاسم الكامل', 'Full Name')}</FieldLabel>
                          <div className="relative">
                            <User className={`w-4.5 h-4.5 text-ink-400 absolute top-1/2 -translate-y-1/2 ${isRTL ? 'right-3.5' : 'left-3.5'}`} />
                            <input
                              type="text"
                              value={form.full_name}
                              onChange={(e) => setField('full_name', e.target.value)}
                              placeholder={t('اكتب اسمك الكامل', 'Type your full name')}
                              className={`w-full h-12 bg-white border-2 border-ink-100 rounded-xl ${isRTL ? 'pr-10 pl-3.5' : 'pl-10 pr-3.5'} text-[14.5px] text-ink-900 placeholder:text-ink-400 focus:border-flame-500 focus:outline-none focus:ring-4 focus:ring-flame-500/12 transition`}
                              data-testid="lead-fullname-input"
                            />
                          </div>
                        </div>

                        <div>
                          <FieldLabel required>{t('البريد الإلكتروني', 'Email')}</FieldLabel>
                          <div className="relative">
                            <Mail className={`w-4.5 h-4.5 text-ink-400 absolute top-1/2 -translate-y-1/2 ${isRTL ? 'right-3.5' : 'left-3.5'}`} />
                            <input
                              type="email"
                              value={form.email}
                              onChange={(e) => setField('email', e.target.value)}
                              placeholder={t('you@example.com', 'you@example.com')}
                              className={`w-full h-12 bg-white border-2 border-ink-100 rounded-xl ${isRTL ? 'pr-10 pl-3.5' : 'pl-10 pr-3.5'} text-[14.5px] text-ink-900 placeholder:text-ink-400 focus:border-flame-500 focus:outline-none focus:ring-4 focus:ring-flame-500/12 transition`}
                              data-testid="lead-email-input"
                              dir="ltr"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <FieldLabel required>{t('الدولة', 'Country')}</FieldLabel>
                            <CountrySelect
                              value={form.country}
                              onChange={(iso) => setField('country', iso)}
                              isRTL={isRTL}
                              testId="lead-country-select"
                            />
                          </div>
                          <div>
                            <FieldLabel required>{t('رقم الهاتف', 'Phone')}</FieldLabel>
                            <PhoneInput
                              value={form.phone}
                              onChange={(v) => setField('phone', v)}
                              defaultIso={form.country || 'SA'}
                              isRTL={isRTL}
                              testId="lead-phone-input"
                            />
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {step === 2 && (
                    <motion.div
                      key="s2"
                      initial={{ opacity: 0, x: isRTL ? -24 : 24 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: isRTL ? 24 : -24 }}
                      transition={{ duration: 0.28 }}
                    >
                      <div className="mb-5">
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-flame-50 text-flame-700 text-[10.5px] font-bold uppercase tracking-[0.15em] mb-2">
                          <TrendingUp className="w-3 h-3" /> {t('الخطوة 2 من 3', 'Step 2 of 3')}
                        </div>
                        <h2 className="text-[22px] md:text-[26px] font-black text-ink-900 leading-tight">
                          {t('ما خبرتك في التداول؟', "What's your trading experience?")}
                        </h2>
                        <p className="text-ink-500 text-[13px] mt-1">
                          {t('سنخصص تجربتك بناءً على إجاباتك.', "We'll tailor your experience to your answers.")}
                        </p>
                      </div>

                      <div className="space-y-2.5 mb-5">
                        {EXPERIENCE_OPTIONS.map((opt) => (
                          <ChoiceCard
                            key={opt.id}
                            active={form.experience === opt.id}
                            onClick={() => setField('experience', opt.id)}
                            icon={opt.icon}
                            title={t(opt.ar, opt.en)}
                            hint={t(opt.hint_ar, opt.hint_en)}
                            testId={`lead-experience-${opt.id}`}
                          />
                        ))}
                      </div>

                      <div className="mb-5">
                        <FieldLabel required>
                          {t('هل تتداول حالياً مع وسيط آخر؟', 'Are you currently with another broker?')}
                        </FieldLabel>
                        <div className="flex flex-wrap gap-2">
                          <PillChoice active={form.other_broker === false} onClick={() => setField('other_broker', false)} testId="lead-other-broker-no">
                            {t('لا، لست متداولاً', 'No, not yet')}
                          </PillChoice>
                          <PillChoice active={form.other_broker === true} onClick={() => setField('other_broker', true)} testId="lead-other-broker-yes">
                            {t('نعم، مع وسيط آخر', 'Yes, with another broker')}
                          </PillChoice>
                        </div>
                        {form.other_broker === true && (
                          <motion.input
                            key="ob-name"
                            initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
                            type="text"
                            value={form.other_broker_name}
                            onChange={(e) => setField('other_broker_name', e.target.value)}
                            placeholder={t('اسم الوسيط (اختياري)', 'Broker name (optional)')}
                            className="mt-2.5 w-full h-11 bg-white border-2 border-ink-100 rounded-xl px-3.5 text-[14px] focus:border-flame-500 focus:outline-none focus:ring-4 focus:ring-flame-500/12 transition"
                            data-testid="lead-other-broker-name"
                          />
                        )}
                      </div>

                      <div>
                        <FieldLabel required>
                          {t('كم المبلغ الذي ترغب باستثماره؟', 'How much would you like to invest?')}
                        </FieldLabel>
                        <div className="flex flex-wrap gap-2">
                          {INVESTMENT_RANGES.map((r) => (
                            <PillChoice
                              key={r.id}
                              active={form.investment_range === r.id}
                              onClick={() => setField('investment_range', r.id)}
                              testId={`lead-budget-${r.id}`}
                            >
                              <span className="inline-flex items-center gap-1.5">
                                <DollarSign className="w-3.5 h-3.5" />
                                {t(r.ar, r.en)}
                              </span>
                            </PillChoice>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {step === 3 && (
                    <motion.div
                      key="s3"
                      initial={{ opacity: 0, x: isRTL ? -24 : 24 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: isRTL ? 24 : -24 }}
                      transition={{ duration: 0.28 }}
                    >
                      <div className="mb-5">
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-flame-50 text-flame-700 text-[10.5px] font-bold uppercase tracking-[0.15em] mb-2">
                          <MessageCircle className="w-3 h-3" /> {t('الخطوة 3 من 3', 'Step 3 of 3')}
                        </div>
                        <h2 className="text-[22px] md:text-[26px] font-black text-ink-900 leading-tight">
                          {t('كيف تفضّل أن نتواصل معك؟', 'How should we reach out?')}
                        </h2>
                        <p className="text-ink-500 text-[13px] mt-1">
                          {t('سيرتب أحد خبرائنا اتصالاً عبر القناة التي تختارها.', "We'll contact you on your preferred channel.")}
                        </p>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mb-5">
                        {CONTACT_OPTIONS.map((c) => {
                          const Icon = c.icon;
                          const active = form.preferred_contact === c.id;
                          return (
                            <button
                              key={c.id}
                              type="button"
                              onClick={() => setField('preferred_contact', c.id)}
                              data-testid={`lead-contact-${c.id}`}
                              className={`p-4 rounded-2xl border-2 transition-all flex flex-col items-center gap-2 ${
                                active
                                  ? 'border-flame-500 bg-flame-50/70 shadow-[0_4px_18px_rgba(216,83,8,0.14)]'
                                  : 'border-ink-100 bg-white hover:border-flame-200 hover:bg-flame-50/30'
                              }`}
                            >
                              <div className={`w-11 h-11 rounded-xl grid place-items-center transition-colors ${
                                active ? 'bg-flame-500 text-white' : 'bg-ink-50 text-ink-700'
                              }`}>
                                <Icon className="w-5 h-5" strokeWidth={1.8} />
                              </div>
                              <span className={`text-[13px] font-extrabold ${active ? 'text-flame-700' : 'text-ink-900'}`}>
                                {t(c.ar, c.en)}
                              </span>
                            </button>
                          );
                        })}
                      </div>

                      <div>
                        <FieldLabel>
                          {t('أي تفاصيل إضافية؟ (اختياري)', 'Anything else? (optional)')}
                        </FieldLabel>
                        <textarea
                          value={form.notes}
                          onChange={(e) => setField('notes', e.target.value)}
                          rows={3}
                          placeholder={t('أهدافك، أسئلتك، أو وقت اتصال مناسب…',
                                         'Your goals, questions, or a good time to call…')}
                          className="w-full bg-white border-2 border-ink-100 rounded-xl p-3.5 text-[14px] text-ink-900 placeholder:text-ink-400 focus:border-flame-500 focus:outline-none focus:ring-4 focus:ring-flame-500/12 transition resize-none"
                          data-testid="lead-notes-input"
                        />
                      </div>

                      <div className="mt-4 flex items-center gap-2 text-[11.5px] text-ink-500">
                        <ShieldCheck className="w-3.5 h-3.5 text-flame-500 shrink-0" />
                        {t('بإرسالك للنموذج توافق على سياسة الخصوصية. لن نشارك بياناتك.',
                           "By submitting you agree to our privacy policy. We won't share your data.")}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {errorMsg && (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
                    className="mt-4 px-3.5 py-2.5 rounded-xl bg-red-50 border border-red-200 text-red-700 text-[12.5px] font-medium"
                    data-testid="lead-modal-error"
                  >
                    {errorMsg}
                  </motion.div>
                )}

                {/* ── Footer actions ──────────────────────────────────── */}
                <div className={`mt-6 flex items-center gap-2.5 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  {step > 1 && (
                    <button
                      type="button"
                      onClick={() => { setErrorMsg(''); setStep(step - 1); }}
                      data-testid="lead-modal-back"
                      className="h-12 px-5 rounded-full bg-ink-50 hover:bg-ink-100 text-ink-700 text-[13.5px] font-bold transition"
                    >
                      {t('السابق', 'Back')}
                    </button>
                  )}
                  <div className="flex-1" />
                  {step < 3 ? (
                    <button
                      type="button"
                      onClick={goNext}
                      data-testid="lead-modal-next"
                      className="h-12 px-7 rounded-full bg-flame-500 hover:bg-flame-600 active:bg-flame-700 text-white text-[14px] font-extrabold tracking-tight transition inline-flex items-center gap-2"
                    >
                      {t('التالي', 'Next')}
                      <ArrowRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={submit}
                      disabled={submitting}
                      data-testid="lead-modal-submit"
                      className="h-12 px-7 rounded-full bg-flame-500 hover:bg-flame-600 active:bg-flame-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-[14px] font-extrabold tracking-tight transition inline-flex items-center gap-2"
                    >
                      {submitting ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> {t('جاري الإرسال…', 'Submitting…')}</>
                      ) : (
                        <><Send className="w-4 h-4" /> {t('إرسال', 'Submit')}</>
                      )}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default LeadCaptureModal;
